import { Button, Grid } from "@chakra-ui/react";
import type { ComponentProps, ReactNode } from "react";
import { DARK_GREEN_GRADIENT } from "../Button/gradients";

type DialogAction = "confirm" | "danger" | "dismiss" | "whatsapp";
type DialogActionButtonSize = "regular" | "compact";
type DialogActionRowLayout = "confirmWide" | "equal";

type ChakraButtonProps = ComponentProps<typeof Button>;
type ChakraGridProps = ComponentProps<typeof Grid>;

interface DialogActionButtonProps extends ChakraButtonProps {
  action: DialogAction;
  buttonSize?: DialogActionButtonSize;
  href?: string;
  target?: string;
  rel?: string;
}

interface DialogActionRowProps extends ChakraGridProps {
  children: ReactNode;
  layout?: DialogActionRowLayout;
}

const RED = "#ef4444";

const buttonSizeStyles: Record<DialogActionButtonSize, Partial<ChakraButtonProps>> = {
  regular: {
    h: "3rem",
    rounded: "0.95rem",
    fontSize: "0.92rem",
  },
  compact: {
    h: "2.6rem",
    rounded: "0.75rem",
    fontSize: "0.82rem",
  },
};

const buttonActionStyles: Record<DialogAction, Partial<ChakraButtonProps>> = {
  confirm: {
    bg: DARK_GREEN_GRADIENT,
    color: "white",
    fontWeight: "800",
    boxShadow: "0 6px 16px rgba(34,197,94,0.24)",
    _hover: {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 24px rgba(34,197,94,0.32)",
    },
  },
  whatsapp: {
    bg: DARK_GREEN_GRADIENT,
    color: "white",
    fontWeight: "800",
    boxShadow: "0 6px 16px rgba(34,197,94,0.24)",
    _hover: {
      transform: "translateY(-1px)",
      boxShadow: "0 10px 24px rgba(34,197,94,0.32)",
    },
  },
  danger: {
    variant: "outline",
    bg: "transparent",
    borderColor: "rgba(239,68,68,0.25)",
    color: RED,
    fontWeight: "700",
    transition: "all 0.15s",
    _hover: {
      bg: "rgba(239,68,68,0.05)",
      borderColor: "rgba(239,68,68,0.4)",
    },
  },
  dismiss: {
    variant: "outline",
    bg: "transparent",
    borderColor: "rgba(15,23,42,0.18)",
    color: "greyscale.text.body",
    fontWeight: "700",
    _hover: {
      bg: "rgba(15,23,42,0.03)",
      borderColor: "rgba(15,23,42,0.3)",
    },
  },
};

export function DialogActionButton({
  action,
  buttonSize = "regular",
  children,
  ...props
}: DialogActionButtonProps) {
  return (
    <Button
      {...buttonSizeStyles[buttonSize]}
      {...buttonActionStyles[action]}
      {...props}
    >
      {children}
    </Button>
  );
}

export function DialogActionRow({
  children,
  layout = "confirmWide",
  gap = "0.7rem",
  ...props
}: DialogActionRowProps) {
  return (
    <Grid
      templateColumns={{
        base: "1fr",
        tablet: layout === "equal" ? "1fr 1fr" : "0.9fr 1.35fr",
      }}
      gap={gap}
      {...props}
    >
      {children}
    </Grid>
  );
}
